import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Button,
    TextField,
    MenuItem,
} from '@mui/material';
import PinkGradientButton from './PinkGradientButton';

const ConfirmationDialog = ({
    open,
    handleClose,
    onConfirm,
    title = 'Are you sure?',
    message = 'Are you sure you want to do something?',
    confirmText,
    cancelText,
    cancelTextReq = true,
    showTextField = false,
    textFieldLabel = 'Enter code',
    textFieldValue,
    onTextFieldChange,
    validate,
    onSave,
    // optional select for multiple choices (e.g. emails from ProVet)
    selectOptions = [], // array of strings
    selectValue = '',
    onSelectChange, // (event) => void
    selectLabel = 'Choose an option',
    includeOtherOption = true, // append "Other…" that caller can use to switch to manual text field
    // optional secondary select for patients
    showSecondarySelect = false,
    secondarySelectOptions = [],
    secondarySelectValue = '',
    onSecondarySelectChange,
    secondarySelectLabel = 'Choose an option',
    hideMiddleButton = false,
    actionJustifyContent = 'flex-end',
}) => {
    const [error, setError] = useState('');

    const handleConfirm = async () => {
        if (validate && showTextField) {
            const isValid = validate(textFieldValue);
            if (!isValid) {
                setError('Invalid input');
                return;
            }
        }
        setError('');
        const confirmResult = await onConfirm();
        if (confirmResult !== false) {
            handleClose();
        }
    };

    const renderBodyField = () => {
        const showSelect = selectOptions && selectOptions.length > 1 && !showTextField;

        if (showSelect) {
            return (
                <TextField
                    select
                    fullWidth
                    label={selectLabel}
                    value={selectValue}
                    onChange={(e) => {
                        setError('');
                        onSelectChange && onSelectChange(e);
                    }}
                    sx={{
                        mt: 2,
                        '& .MuiSelect-select': {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        },
                    }}
                    SelectProps={{
                        MenuProps: {
                            PaperProps: {
                                style: {
                                    maxWidth: '90%',
                                },
                            },
                        },
                    }}
                >
                    {selectOptions.map((opt) => (
                        <MenuItem key={opt} value={opt}>
                            {opt}
                        </MenuItem>
                    ))}
                    {includeOtherOption && <MenuItem value="__OTHER__">Other…</MenuItem>}
                </TextField>
            );
        }

        if (showTextField) {
            return (
                <TextField
                    autoFocus
                    margin="dense"
                    label={textFieldLabel}
                    type="text"
                    fullWidth
                    value={textFieldValue}
                    onChange={(e) => {
                        onTextFieldChange && onTextFieldChange(e);
                        setError('');
                    }}
                    error={!!error}
                    helperText={error}
                    sx={{ mt: 2 }}
                />
            );
        }

        return null;
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            aria-labelledby="confirmation-dialog"
            PaperProps={{
                className: 'dialog-container',
                sx: {
                    p: { xs: 2, sm: 4 },
                    minWidth: { xs: 'auto', sm: '500px' },
                    maxWidth: '711px',
                    borderRadius: 4,
                    width: { xs: 'calc(100% - 32px)', sm: 'auto' },
                    m: { xs: 2, sm: 4 },
                },
            }}
        >
            <DialogTitle
                variant="h4"
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    p: 0,
                    fontWeight: 600,
                }}
            >
                {title}
            </DialogTitle>
            <DialogContent sx={{ p: 0, mt: 3, mb: 4 }}>
                <Typography variant="body1" sx={{ color: '#616063' }}>
                    {message}
                </Typography>
                {renderBodyField()}
                {showSecondarySelect && secondarySelectOptions.length > 0 && (
                    <TextField
                        select
                        fullWidth
                        label={secondarySelectLabel}
                        value={secondarySelectValue}
                        onChange={(e) => {
                            setError('');
                            onSecondarySelectChange && onSecondarySelectChange(e);
                        }}
                        sx={{
                            mt: 2,
                            '& .MuiSelect-select': {
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            },
                        }}
                        SelectProps={{
                            MenuProps: {
                                PaperProps: {
                                    style: {
                                        maxWidth: '90%',
                                    },
                                },
                            },
                        }}
                    >
                        {secondarySelectOptions.map((opt) => (
                            <MenuItem key={opt} value={opt}>
                                {opt}
                            </MenuItem>
                        ))}
                    </TextField>
                )}
            </DialogContent>
            <DialogActions
                sx={{
                    p: 0,
                    mt: { xs: 0, sm: 3 },
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: 'center',
                    justifyContent: actionJustifyContent,
                }}
            >
                <PinkGradientButton
                    onClick={handleConfirm}
                    variant="contained"
                    sx={{
                        my: { xs: 1, sm: 1.5 },
                        mr: { sm: 2 },
                        background: '#FF669B',
                        backgroundImage: 'none',
                        '&:hover': { background: '#FF85B0', backgroundImage: 'none' },
                        color: '#fff',
                    }}
                >
                    {confirmText ? confirmText : 'Yes'}
                </PinkGradientButton>

                {onSave && !hideMiddleButton && (
                    <PinkGradientButton
                        onClick={() => {
                            onSave();
                            handleClose();
                        }}
                        variant="contained"
                        sx={{ my: { xs: 1, sm: 1.5 }, mr: { sm: 2 } }}
                    >
                        Save & Exit
                    </PinkGradientButton>
                )}

                {cancelTextReq && (
                    <Button
                        onClick={handleClose}
                        className="black-btn"
                        sx={{
                            color: '#0b0c26',
                            textDecoration: 'underline',
                            textTransform: 'none',
                            fontWeight: 600,
                            height: { xs: 'auto', sm: 48 },
                            mt: { xs: 1, sm: 0 },
                        }}
                    >
                        {cancelText ? cancelText : 'No'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default ConfirmationDialog;
